import { useCallback, useEffect, useState } from 'react'
import { userRepository } from '@/data/users/repositories/user_repository_impl'
import { pushNotificationRepository } from '@/data/push_notifications/repositories/push_notification_repository_impl'
import type { UserListItem } from '@/domain/users/entities/user.entity'
import { GetUsersUseCase } from '@/domain/users/usecases/get_users.usecase'
import { SendPushToUserUseCase } from '@/domain/push_notifications/usecases/send_push_to_user.usecase'
import { SendPushToAllUseCase } from '@/domain/push_notifications/usecases/send_push_to_all.usecase'
import { mapSendToAllFromStrings } from '@/data/push_notifications/mappers/push_notification.mapper'
import { toAppError } from '@/core/errors/app_error'
import { logError } from '@/utils/logger'
import { matchPushRecipient } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'
import {
  initialModalState,
  type PushRecipientSortField,
  type PushNotificationsModalState,
} from '../models/push_notifications_ui_state'

const defaultGetUsers = new GetUsersUseCase(userRepository)
const defaultSendToUser = new SendPushToUserUseCase(pushNotificationRepository)
const defaultSendToAll = new SendPushToAllUseCase(pushNotificationRepository)

export interface UsePushNotificationsViewModelDeps {
  getUsers?: GetUsersUseCase
  sendToUser?: SendPushToUserUseCase
  sendToAll?: SendPushToAllUseCase
}

export function usePushNotificationsViewModel(deps: UsePushNotificationsViewModelDeps = {}) {
  const getUsers = deps.getUsers ?? defaultGetUsers
  const sendToUser = deps.sendToUser ?? defaultSendToUser
  const sendToAll = deps.sendToAll ?? defaultSendToAll

  const [users, setUsers] = useState<UserListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sendingNotification, setSendingNotification] = useState<string | null>(null)
  const [sendingToAll, setSendingToAll] = useState(false)
  const [modals, setModals] = useState<PushNotificationsModalState>(initialModalState)

  const { table, actions: tableActions } = useTableQueryState({
    items: users,
    matchesSearch: matchPushRecipient,
  })

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const list = await getUsers.execute({ page: 0, limit: 100 })
      setUsers(list)
    } catch (err: unknown) {
      logError('Failed to fetch users', err)
      setError(toAppError(err, 'Failed to load users').message)
      setUsers([])
    } finally {
      setIsLoading(false)
    }
  }, [getUsers])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openIndividualModal = useCallback((user: UserListItem) => {
    setModals({ showIndividualModal: true, showSendToAllModal: false, selectedUser: user })
  }, [])

  const closeIndividualModal = useCallback(() => {
    setModals(prev => ({ ...prev, showIndividualModal: false, selectedUser: null }))
  }, [])

  const openSendToAllModal = useCallback(() => {
    setModals(prev => ({ ...prev, showSendToAllModal: true }))
  }, [])

  const closeSendToAllModal = useCallback(() => {
    setModals(prev => ({ ...prev, showSendToAllModal: false }))
  }, [])

  const sendIndividualNotification = useCallback(
    async (title: string, message: string) => {
      const selectedUser = modals.selectedUser
      if (!selectedUser) return

      setSendingNotification(selectedUser.id)
      try {
        await sendToUser.execute(selectedUser.id, { title, message })
        alert(`Notification sent to ${selectedUser.name}`)
        closeIndividualModal()
      } catch (err: unknown) {
        logError('Failed to send notification', err)
        alert('Failed to send notification. Please try again.')
        throw err
      } finally {
        setSendingNotification(null)
      }
    },
    [closeIndividualModal, modals.selectedUser, sendToUser]
  )

  const sendToAllNotification = useCallback(
    async (userType: string, title: string, message: string) => {
      setSendingToAll(true)
      try {
        const request = mapSendToAllFromStrings(userType, title, message)
        await sendToAll.execute(request)
        alert(`Notification sent to all ${userType === 'All' ? 'users' : `${userType} users`}`)
        closeSendToAllModal()
      } catch (err: unknown) {
        logError('Failed to send notification to all', err)
        alert('Failed to send notification to all users. Please try again.')
        throw err
      } finally {
        setSendingToAll(false)
      }
    },
    [closeSendToAllModal, sendToAll]
  )

  const handleSort = useCallback(
    (field: PushRecipientSortField) => tableActions.handleSort(field),
    [tableActions]
  )

  return {
    state: {
      users,
      filteredUsers: table.filteredItems,
      paginatedUsers: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      modals,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    error,
    sendingNotification,
    sendingToAll,
    actions: {
      refresh,
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort,
      openIndividualModal,
      closeIndividualModal,
      openSendToAllModal,
      closeSendToAllModal,
      sendIndividualNotification,
      sendToAllNotification,
    },
  }
}
